import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getFormattedPriceObject } from '@/helpers/currencyHelpers';
import { haversine } from '@/helpers/locationHelpers';
import { useFetchSpaceSubtypeRelationshipById } from '@/services/spacesSubtypeRelationshipService';
import { useFetchPostCommentById } from '@/services/postCommentsCopyService';
import { getOAuthTokenGoogle } from '@/services/userService';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';

import {
  MMAXIMUM_DISTANCE_SEARCH_SPACES,
  DEFAULT_MAP_COORDINATES,
  MAX_DISTANCE_THRESHOLD,
} from '@/types/distance.constants';

export const useSpacesDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { idSubSpace, customText } = useParams();

  const searchFormData = useSelector(state => state.searchFormData);
  const currencyData = useSelector(state => state.currency);
  const location = useSelector(state => state.location?.data);

  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  const [spacesDataFromBackend, setSpacesDataFromBackend] = useState([]);
  const [coordinatesForMapCenter, setCoordinatesForMapCenter] = useState({});
  const [isSignInModalIsOpen, setIsSignInModalIsOpen] = useState(false);
  const [isWarningModalIsOpen, setIsWarningModalIsOpen] = useState(true);
  const [isScrollListenerActive, setIsScrollListenerActive] = useState(true);

  const { data, error } = useFetchSpaceSubtypeRelationshipById(idSubSpace);

  const { data: commentsData, error: commentsError } =
    useFetchPostCommentById(idSubSpace);

  const goToWorkspaceMap = () => {
    navigate('/');
  };

  const getCoordinatesForMapCenter = (coordinatesFromFormData, spacesData) => {
    const filteredByType = spacesData?.filter(
      space =>
        space?.subtype_title.toLowerCase() ===
        searchFormData?.spaceType?.toLowerCase(),
    );

    if (!filteredByType || filteredByType.length === 0) {
      return coordinatesFromFormData;
    }

    const closestSpace = filteredByType.reduce((prev, current) => {
      const currentDist = haversine(
        coordinatesFromFormData.lat,
        coordinatesFromFormData.lng,
        parseFloat(current?.space_latitude),
        parseFloat(current?.space_logitude),
      );

      const prevDist =
        prev.distance !== undefined
          ? prev.distance
          : haversine(
              coordinatesFromFormData.lat,
              coordinatesFromFormData.lng,
              parseFloat(prev?.space_latitude),
              parseFloat(prev?.space_logitude),
            );

      return currentDist < prevDist
        ? { ...current, distance: currentDist }
        : { ...prev, distance: prevDist };
    });

    if (closestSpace.distance < MAX_DISTANCE_THRESHOLD) {
      return {
        lat: parseFloat(closestSpace.space_latitude),
        lng: parseFloat(closestSpace.space_logitude),
      };
    }

    return coordinatesFromFormData;
  };

  const formatSpacesData = (coordinates, spacesData) => {
    const filteredSpacesByDistance = spacesData
      ?.filter(
        space =>
          space?.subtype_title.toLowerCase() ===
          searchFormData?.spaceType?.toLowerCase(),
      )
      ?.map(space => {
        const distance = haversine(
          coordinates.lat,
          coordinates.lng,
          parseFloat(space?.space_latitude),
          parseFloat(space?.space_logitude),
        );

        return { ...space, distance };
      })
      ?.filter(space => space?.distance <= MMAXIMUM_DISTANCE_SEARCH_SPACES)
      .sort((a, b) => a.distance - b.distance);

    const selectedId = idSubSpace || searchFormData?.seletedSpaceId;

    const sortedData = selectedId
      ? [...filteredSpacesByDistance].sort((a, b) => {
          if (a.id_relationship === Number(selectedId)) return -1;
          if (b.id_relationship === Number(selectedId)) return 1;
          return 0;
        })
      : filteredSpacesByDistance;

    return sortedData.map(space => {
      const formattedPriceObject = getFormattedPriceObject(
        space,
        currencyData?.currency,
      );

      return {
        ...space,
        calculatedPrice: formattedPriceObject?.calculatedPrice,
        currency: formattedPriceObject?.currency,
        formattedPrice: formattedPriceObject?.formattedPrice,
      };
    });
  };

  const onClickMarkers = (id, coordinates) => {
    dispatch(
      setSearchFormData({
        ...searchFormData,
        coordinates: coordinates,
        seletedSpaceId: id,
      }),
    );
  };

  const loginWithGoogleOAuth = async ({ username, googleToken }) => {
    setLoading(true);
    setErrors(null);

    try {
      const tokenData = await getOAuthTokenGoogle({
        username,
        googleToken,
      });

      return tokenData;
    } catch (err) {
      console.error('Error en loginWithGoogleOAuth:', err);
      setErrors(err.message || 'Google OAuth failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, on) => {
    setLoading(true);
    setErrors(null);

    try {
      await on(values);
    } catch (err) {
      setErrors('Invalid booking success. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openSignInModal = () => {
    setIsSignInModalIsOpen(true);
  };

  const closeSignInModal = () => {
    setIsSignInModalIsOpen(false);
  };

  const closeWarningModal = () => {
    setIsWarningModalIsOpen(false);
  };

  useEffect(() => {
    if (data) {
      const space = Array.isArray(data) ? data[0] : data;

      const formattedPriceObject = getFormattedPriceObject(
        space,
        currencyData?.currency,
      );

      const formattedSpace = {
        ...space,
        calculatedPrice: formattedPriceObject?.calculatedPrice,
        currency: formattedPriceObject?.currency,
        formattedPrice: formattedPriceObject?.formattedPrice,
      };

      const calculatedCoords = {
        lat: parseFloat(space.space_latitude),
        lng: parseFloat(space.space_logitude),
      };

      setSpacesDataFromBackend([formattedSpace]);
      setCoordinatesForMapCenter(calculatedCoords);
      setIsLoading(false);
    }

    if (error) {
      setIsLoading(false);
    }
  }, [data, currencyData, error]);

  return {
    isLoading,
    loading,
    errors,
    spacesDataFromBackend,
    isSignInModalIsOpen,
    openSignInModal,
    closeSignInModal,
    closeWarningModal,
    isWarningModalIsOpen,
    coordinatesForMapCenter,
    onClickMarkers,
    loginWithGoogleOAuth,
    handleSubmit,
    commentsData,
    commentsError,
    goToWorkspaceMap,
  };
};
